import React, { useRef, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { X, PenLine, Type, Eraser, Loader2, FileText, ShieldCheck } from 'lucide-react';
import { BACKEND_URL } from '../../config/canonical';

const TOKEN_KEY = 'nexus_token';
const APPLICANT_TOKEN_KEY = 'nexus_applicant_token';

const getToken = () =>
  localStorage.getItem(APPLICANT_TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);

/**
 * DocuSign-style signing modal.
 *
 * Props:
 *   assignment: { id, template_name, template_id, sent_at, status, ... }
 *   onClose:    () => void
 *   onSigned:   () => void   (called after successful sign)
 */
const SignDocumentModal = ({ assignment, onClose, onSigned }) => {
  // Iter132 — typed-signature only (canvas draw removed per user directive).
  const [typedName, setTypedName] = useState('');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfErr, setPdfErr] = useState(null);

  // Load original PDF for preview.  Iter132 — if the applicant-scoped
  // route 404s (common when a hiring doc wasn't materialized yet),
  // fall back to the admin template preview route so built-in policies
  // like "Operational Order Policy" and "Case Acceptance Policy" still
  // open instead of showing "Preview failed: HTTP 404".
  useEffect(() => {
    if (!assignment?.id) return;
    let url = null;
    let alive = true;
    (async () => {
      try {
        const token = getToken();
        let res = await fetch(
          `${BACKEND_URL}/api/applicant-portal/document-assignments/${assignment.id}/file`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.status === 404 && assignment?.template_id) {
          // Admin / preview context: try the template file endpoint.
          res = await fetch(
            `${BACKEND_URL}/api/admin/document-templates/${assignment.template_id}/file`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
        if (res.status === 404 && assignment?.template_id) {
          // Onboarding templates library fallback (NexusNewHire pages).
          res = await fetch(
            `${BACKEND_URL}/api/admin/onboarding-templates/${assignment.template_id}/view`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
        if (!res.ok) throw new Error(`PDF not available (HTTP ${res.status})`);
        const blob = await res.blob();
        url = URL.createObjectURL(blob);
        if (alive) setPdfUrl(url);
      } catch (e) {
        if (alive) setPdfErr(e.message || 'Failed to load document');
      }
    })();
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [assignment?.id, assignment?.template_id]);

  // Render typed signature into a canvas (script font) → PNG dataURL.
  const buildTypedSignatureCanvas = () => {
    const c = document.createElement('canvas');
    c.width = 600; c.height = 180;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.fillStyle = '#0F172A';
    ctx.font = 'italic 64px "Brush Script MT", "Lucida Handwriting", cursive';
    ctx.textBaseline = 'middle';
    ctx.fillText(typedName.trim() || ' ', 20, c.height / 2);
    return c;
  };

  const handleSubmit = async () => {
    if (!agree) { toast.error('You must agree to electronically sign this document'); return; }
    const finalName = typedName.trim();
    if (finalName.length < 2) { toast.error('Type your full legal name (2+ characters)'); return; }
    const canvas = buildTypedSignatureCanvas();
    const dataUrl = canvas.toDataURL('image/png');

    // Build per-field values from any placed template fields:
    // text fields get the signer's name; date fields get auto-filled by backend.
    const tplFields = Array.isArray(assignment?.template_fields) ? assignment.template_fields : [];
    const fieldValues = {};
    tplFields.forEach(f => {
      if (f.type === 'text') fieldValues[f.id] = finalName;
    });

    setBusy(true);
    try {
      const token = getToken();
      const res = await fetch(
        `${BACKEND_URL}/api/applicant-portal/document-assignments/${assignment.id}/sign`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            signature_image_base64: dataUrl,
            signed_name: finalName,
            agree: true,
            field_values: fieldValues,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      toast.success('Document signed successfully');
      onSigned?.();
      onClose?.();
    } catch (e) {
      toast.error(`Sign failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-950/85 backdrop-blur-sm flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="sign-doc-modal"
    >
      <div className="bg-white rounded-lg w-full max-w-4xl shadow-2xl flex flex-col max-h-[96vh]">
        {/* Header */}
        <div className="border-b border-slate-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.25em] text-amber-600 font-bold">Sign Document</p>
            <h2 className="text-slate-900 text-base font-bold truncate">{assignment?.template_name || 'Document'}</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 flex-shrink-0 ml-2" data-testid="sign-doc-close">
            <X size={20} />
          </button>
        </div>

        {/* Body: PDF preview + signature pad */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 bg-slate-50">
          {/* PDF Preview */}
          <div className="bg-white border border-slate-300 rounded">
            <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-2 text-xs text-slate-600">
              <FileText size={14} /> Document Preview
            </div>
            {pdfErr ? (
              <p className="p-4 text-rose-700 text-sm">Failed to load document: {pdfErr}</p>
            ) : !pdfUrl ? (
              <p className="p-4 text-slate-500 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading PDF…</p>
            ) : (
              <iframe
                src={pdfUrl}
                title="Document Preview"
                className="w-full h-[420px] sm:h-[480px] border-0"
                data-testid="sign-doc-pdf-preview"
              />
            )}
          </div>

          {/* Signature pad — typed-only (Iter132) */}
          <div className="bg-white border border-slate-300 rounded p-3">
            <p className="text-xs font-semibold text-slate-800 mb-2">Type your full legal name below. This is your legally-binding electronic signature.</p>
            {Array.isArray(assignment?.template_fields) && assignment.template_fields.length > 0 && (
              <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mb-2" data-testid="sign-fields-hint">
                This document has {assignment.template_fields.length} signature/initial/date/text field(s).
                Your typed signature will be placed on every signature & initials field automatically.
              </p>
            )}
            <div className="border-2 border-dashed border-slate-400 rounded bg-white p-4">
              <input
                type="text"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder="Type your full legal name…"
                className="w-full text-3xl text-slate-900 italic bg-transparent border-0 outline-none p-0 text-center"
                style={{ fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive' }}
                data-testid="sign-typed-name"
                autoFocus
              />
            </div>
            <p className="text-[11px] text-slate-500 mt-1 text-center">Preview of your signature</p>

            <label className="flex items-start gap-2 mt-3 text-xs text-slate-700 cursor-pointer" data-testid="sign-agree-label">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-0.5"
                data-testid="sign-agree"
              />
              <span>
                By checking this box, I agree that my electronic signature is the legal equivalent
                of my manual/handwritten signature on this document and consent to be legally bound
                by its terms.
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-4 py-3 flex items-center justify-end gap-2 flex-shrink-0 bg-white rounded-b-lg">
          <Button variant="ghost" onClick={onClose} disabled={busy} className="text-slate-700 h-9" data-testid="sign-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={busy || !agree}
            className="bg-emerald-600 hover:bg-emerald-500 text-white h-9 font-semibold"
            data-testid="sign-submit"
          >
            {busy ? <Loader2 className="animate-spin" size={14} /> : <ShieldCheck size={14} />}
            <span className="ml-1.5">Sign & Submit</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SignDocumentModal;
